import FFLogo from 'assets/icons/ff-logo.svg?component'
import DamuLogoDark from 'assets/images/landing/damu-logo-dark.svg?component'
import DamuLogoLight from 'assets/images/landing/damu-logo-light.svg?component'
import InfoBlock from 'components/UI/Blocks/InfoBlock'
import Button from 'components/UI/Buttons/Button'
import Typography from 'components/UI/Typography'
import Heading from 'components/UI/Typography/Heading'
import { useTheme } from 'contexts/theme-context'
import { useCreditDecision } from 'hooks/useCreditDecision'
import { TFunction } from 'i18next'
import { Trans, useTranslation } from 'react-i18next'
import { type ApplicationProgramCode } from 'share/types'
import type { ScoringDefinedCreditProgram } from 'share/types/api/credits'
import tw from 'twin.macro'

const getProgramTitle = (code: ApplicationProgramCode) => {
  switch (code) {
    case 'OWN_PROGRAM':
      return 'decision-page.own'
    case 'DAMU':
      return 'decision-page.damu'
  }
}

const getProgramStatus = (length: number) => {
  if (length === 0) {
    return {
      text: 'decision-page.rejected',
      element: <span tw='text-red-light' />
    }
  }
  return {
    text: 'decision-page.approved',
    element: <span tw='text-primary' />
  }
}

const getRateText = (code: ApplicationProgramCode, programs: Array<ScoringDefinedCreditProgram>, t: TFunction) => {
  if (code === 'DAMU') {
    return t('without-pledge')
  }
  if (code === 'OWN_PROGRAM') {
    return programs
      .filter(p => Object.keys(p.options).length)
      .map(p => (!p.with_pledges ? t('without-pledge') : t('with-pledge')))
      .join(' / ')
  }
}

const LoanProgramSelection = () => {
  // Tools
  const { t } = useTranslation()

  // Hooks
  const { programs, prepareLoanParamsView } = useCreditDecision()
  const { theme } = useTheme()

  return (
    <div className='flex flex-col gap-y-3'>
      {programs.data?.map(program => {
        const { element, text } = getProgramStatus(program.programs.length)
        return (
          <InfoBlock key={program.program_code} twStyle={tw`flex flex-col`}>
            <div className='flex items-center gap-x-8'>
              <Heading variant='subtitle' weight='bold'>
                <Trans
                  components={{
                    span: element
                  }}
                  values={{
                    program: t(getProgramTitle(program.program_code)),
                    programStatus: t(text)
                  }}
                >
                  {t('decision-page.program-text')}
                </Trans>
              </Heading>
              {program.program_code === 'OWN_PROGRAM' ? (
                <FFLogo tw='text-[var(--text-main)] flex-shrink-0 ml-auto h-8' />
              ) : program.program_code === 'DAMU' ? (
                theme === 'dark' ? (
                  <DamuLogoDark width={'126px'} tw=' ml-auto flex-shrink-0' />
                ) : (
                  <DamuLogoLight width={'126px'} tw=' ml-auto flex-shrink-0' />
                )
              ) : null}
            </div>
            {program.program_code === 'DAMU' && !program.programs.length && (
              <p tw='mt-5 text-red-light text-sm'>{t('damu-reject')}</p>
            )}
            {!program.programs.length ? null : (
              <Typography variant='subbody' twStyle={tw`mt-4 mb-5`}>
                {program.program_code === 'OWN_PROGRAM'
                  ? t('decision-page.own-text')
                  : program.program_code === 'DAMU'
                  ? t('decision-page.damu-text')
                  : null}
              </Typography>
            )}
            {!program.programs.length ? null : (
              <div tw='grid grid-cols-1 md:grid-cols-3'>
                <div tw='py-2 px-3 rounded-[6px] bg-toggle-button'>
                  <Heading variant='subtitle' weight='bold'>
                    {program.interestRate}
                  </Heading>
                  <Typography variant='caption' twStyle={tw`text-grey-dark lowercase`}>
                    <Trans
                      values={{
                        text: getRateText(program.program_code, program.programs, t)
                      }}
                    >
                      {t('decision-page.rate-text')}
                    </Trans>
                  </Typography>
                </div>
                <div className='py-2 px-3'>
                  <Heading variant='subtitle'>
                    <Trans
                      values={{
                        loan: program.maxLoanAmount
                      }}
                    >
                      {t('decision-page.loan-limit')}
                    </Trans>
                  </Heading>
                  <Typography variant='caption' twStyle={tw`text-grey-dark`}>
                    {t('decision-page.loan-amount')}
                  </Typography>
                </div>
                <div className='py-2 px-3'>
                  <Heading variant='subtitle'>
                    <Trans values={{ term: program.maxPeriod }}>{t('decision-page.term-limit')}</Trans>
                  </Heading>
                  <Typography variant='caption' twStyle={tw`text-grey-dark`}>
                    {t('loan.period', { ns: 'common' })}
                  </Typography>
                </div>
              </div>
            )}
            {!program.programs.length ? null : (
              <Button
                variant='secondary'
                twStyle={tw`self-end w-[fit-content] min-w-[unset] mt-5 py-3 px-5`}
                disabled={!program.programs.length}
                onClick={() => prepareLoanParamsView(program.program_code)}
              >
                <Typography variant='subbody' weight='bold'>
                  {t('flow-choose')}
                </Typography>
              </Button>
            )}
          </InfoBlock>
        )
      })}
    </div>
  )
}

export default LoanProgramSelection
